import { Button } from '@game/shared';
import type { InputManager } from './input.js';

/**
 * Touch controls, modelled on the scheme Roblox-style mobile shooters ship on
 * phones: the left half of the screen is a dynamic thumbstick that appears
 * wherever the thumb lands, dragging anywhere else steers the camera, and the
 * actual actions — jump, fire, crouch, reload — are on-screen buttons rendered
 * by React (`ui/touch.tsx`) that poke bits into the InputManager. Auto-fire
 * (shoot when the crosshair is on an enemy) lives in the engine, not here: it
 * needs the renderer's enemy positions and the map's collision.
 *
 * Everything funnels into the same `InputCommand` the keyboard produces, so the
 * sim, the netcode and the server never know a finger was involved.
 */

/**
 * Decided once at startup: a coarse primary pointer is a phone or tablet.
 * `?touch` forces it on, which is how the layout is tested from a desktop.
 */
export const TOUCH_MODE =
  typeof window !== 'undefined' &&
  (window.matchMedia?.('(pointer: coarse)').matches === true ||
    new URLSearchParams(window.location.search).has('touch'));

/** Thumb travel from the stick's origin to full deflection, in CSS pixels. */
const JOYSTICK_RADIUS = 56;
/** Deflection below this is a resting thumb, not a move. */
const DEAD_ZONE = 0.25;
/** Deflection past this sprints — auto-sprint, the mobile default. */
const SPRINT_ZONE = 0.9;

/** Long-press context menus and text-selection callouts have no place here. */
const preventDefault = (event: Event) => event.preventDefault();

/**
 * Quantises a thumbstick vector into the wire's digital move bits. `x` is
 * right-positive, `y` is forward-positive, both normalised to the stick radius.
 * Eight directions is what a WASD protocol can say; the ±22.5° rounding is
 * imperceptible mid-fight and keeps the command identical to a keyboard's.
 */
export const joystickButtons = (x: number, y: number): number => {
  const magnitude = Math.hypot(x, y);
  if (magnitude < DEAD_ZONE) return 0;

  // 0 = forward, positive = clockwise, in eighths of a turn.
  const sector = Math.round(Math.atan2(x, y) / (Math.PI / 4));
  let buttons: number;
  switch (sector) {
    case 0:
      buttons = Button.Forward;
      break;
    case 1:
      buttons = Button.Forward | Button.Right;
      break;
    case 2:
      buttons = Button.Right;
      break;
    case 3:
      buttons = Button.Back | Button.Right;
      break;
    case -1:
      buttons = Button.Forward | Button.Left;
      break;
    case -2:
      buttons = Button.Left;
      break;
    case -3:
      buttons = Button.Back | Button.Left;
      break;
    default:
      buttons = Button.Back;
      break;
  }
  if (magnitude >= SPRINT_ZONE) buttons |= Button.Sprint;
  return buttons;
};

/**
 * Owns the raw touch surface: the dynamic joystick on the left half of the
 * canvas and the look-drag everywhere else. The joystick's base and nub are
 * plain DOM nodes it positions imperatively — they move with the thumb many
 * times a second, which is exactly what React must stay out of.
 */
export class TouchControls {
  private movePointer: number | null = null;
  private lookPointer: number | null = null;
  private originX = 0;
  private originY = 0;
  private lastLookX = 0;
  private lastLookY = 0;

  private readonly root: HTMLDivElement;
  private readonly base: HTMLDivElement;
  private readonly nub: HTMLDivElement;
  private readonly disposers: (() => void)[] = [];

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly input: InputManager,
  ) {
    this.root = document.createElement('div');
    this.root.style.cssText =
      'position:fixed;inset:0;pointer-events:none;z-index:5;display:none';
    this.base = document.createElement('div');
    this.base.style.cssText =
      `position:absolute;width:${JOYSTICK_RADIUS * 2}px;height:${JOYSTICK_RADIUS * 2}px;` +
      `margin:-${JOYSTICK_RADIUS}px 0 0 -${JOYSTICK_RADIUS}px;` +
      'border-radius:50%;border:2px solid rgba(255,255,255,0.25);' +
      'background:rgba(255,255,255,0.06)';
    this.nub = document.createElement('div');
    this.nub.style.cssText =
      'position:absolute;width:48px;height:48px;margin:-24px 0 0 -24px;border-radius:50%;' +
      'background:rgba(255,255,255,0.35)';
    this.root.append(this.base, this.nub);
  }

  attach(): void {
    document.body.append(this.root);
    this.canvas.style.touchAction = 'none';

    // Touches on the bare canvas obviously steer the game — but so must drags
    // that *start* on the on-screen action buttons, which sit exactly where the
    // right thumb naturally rests. Without this, a player whose look-drag lands
    // on the fire cluster gets a dead camera (and drag-to-aim while holding
    // fire, standard on mobile shooters, is impossible). The buttons opt in via
    // `data-look-through`; every other overlay (pause, menus, votes) keeps its
    // touches to itself.
    const claimable = (target: EventTarget | null): boolean =>
      target === this.canvas ||
      (target instanceof Element && target.closest('[data-look-through]') !== null);

    // Any pointer type: fingers on a phone, but also the mouse when touch mode
    // is forced with ?touch on a desktop — otherwise "testing" the layout is a
    // dead canvas — and pens/trackpads on hybrids where TOUCH_MODE is on.
    const down = (event: PointerEvent) => {
      // iOS reuses pointer ids, and it can eat the up/cancel of a touch it
      // turned into a system gesture — which left the joystick stuck mid-air,
      // walking the player forward with no finger down. A new touch wearing a
      // claimed id means exactly that: the old claim is stale, release it
      // before claiming anew.
      if (event.pointerId === this.movePointer) this.releaseMove();
      if (event.pointerId === this.lookPointer) this.lookPointer = null;
      if (!claimable(event.target)) return;
      if (event.target === this.canvas) {
        event.preventDefault();
        // A mouse needs explicit capture to keep move events flowing off the
        // canvas; touches are implicitly captured by whatever they land on, and
        // their moves bubble up to this window listener either way.
        if (event.pointerType === 'mouse') {
          this.canvas.setPointerCapture(event.pointerId);
        }
      }
      // Only a bare-canvas touch on the left half spawns the joystick — a drag
      // off a button is always a look, never a phantom walk.
      if (
        this.movePointer === null &&
        event.target === this.canvas &&
        event.clientX < window.innerWidth / 2
      ) {
        this.movePointer = event.pointerId;
        this.originX = event.clientX;
        this.originY = event.clientY;
        this.showJoystick(event.clientX, event.clientY);
      } else if (this.lookPointer === null) {
        this.lookPointer = event.pointerId;
        this.lastLookX = event.clientX;
        this.lastLookY = event.clientY;
      }
    };

    const move = (event: PointerEvent) => {
      if (event.pointerId === this.movePointer) {
        let dx = event.clientX - this.originX;
        let dy = event.clientY - this.originY;
        const length = Math.hypot(dx, dy);
        if (length > JOYSTICK_RADIUS) {
          dx *= JOYSTICK_RADIUS / length;
          dy *= JOYSTICK_RADIUS / length;
        }
        this.nub.style.left = `${this.originX + dx}px`;
        this.nub.style.top = `${this.originY + dy}px`;
        // Screen y grows downward; the stick's forward is up.
        this.input.setTouchMove(
          joystickButtons(dx / JOYSTICK_RADIUS, -dy / JOYSTICK_RADIUS),
        );
      } else if (event.pointerId === this.lookPointer) {
        this.input.applyTouchLook(
          event.clientX - this.lastLookX,
          event.clientY - this.lastLookY,
        );
        this.lastLookX = event.clientX;
        this.lastLookY = event.clientY;
      }
    };

    const up = (event: PointerEvent) => {
      if (event.pointerId === this.movePointer) {
        this.releaseMove();
      } else if (event.pointerId === this.lookPointer) {
        this.lookPointer = null;
      }
    };

    // Backstop for ups the browser never delivers at all (tab switch,
    // notification shade, home-swipe): losing focus or visibility drops every
    // claim, so the worst case is a re-press, never a phantom walk.
    const releaseAll = () => {
      this.releaseMove();
      this.lookPointer = null;
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') releaseAll();
    };

    // Window, not canvas: a pointer that starts on a look-through button never
    // targets the canvas at all, and a captured touch's events retarget to the
    // element that owns it — the window is the one place that sees them all.
    window.addEventListener('pointerdown', down);
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
    window.addEventListener('blur', releaseAll);
    document.addEventListener('visibilitychange', onVisibility);
    this.canvas.addEventListener('contextmenu', preventDefault);
    this.disposers.push(
      () => window.removeEventListener('pointerdown', down),
      () => window.removeEventListener('pointermove', move),
      () => window.removeEventListener('pointerup', up),
      () => window.removeEventListener('pointercancel', up),
      () => window.removeEventListener('blur', releaseAll),
      () => document.removeEventListener('visibilitychange', onVisibility),
      () => this.canvas.removeEventListener('contextmenu', preventDefault),
    );
  }

  /** Drops the joystick claim and zeroes the move bits — never leaves a phantom walk. */
  private releaseMove(): void {
    this.movePointer = null;
    this.input.setTouchMove(0);
    this.root.style.display = 'none';
  }

  private showJoystick(x: number, y: number): void {
    this.base.style.left = `${x}px`;
    this.base.style.top = `${y}px`;
    this.nub.style.left = `${x}px`;
    this.nub.style.top = `${y}px`;
    this.root.style.display = 'block';
  }

  dispose(): void {
    for (const dispose of this.disposers) dispose();
    this.disposers.length = 0;
    this.root.remove();
    this.input.setTouchMove(0);
  }
}
